import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * The contact form (snippets/contact-form-hub.liquid).
 *
 * Submits to the Cornelius app's App Proxy, which forwards to the hub with a
 * signed query. The hub opens an action for the team and emails the customer
 * an acknowledgement. Shopify's own contact form is not used.
 */
class ContactFormHub extends Component {
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleSubmit);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.#busy) return;

    const button = form.querySelector('button[type="submit"]');
    const data = new FormData(form);
    /** @param {string} name */
    const field = (name) => String(data.get(name) ?? '').trim();
    const body = {
      topic: field('topic'),
      name: field('name'),
      email: field('email'),
      phone: field('phone'),
      message: field('message'),
      company: field('company'),
    };

    this.#showError(form, '');
    this.#busy = true;
    button?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(form.action, fetchConfig('json', { body: JSON.stringify(body) }));
      const answer = await response.json().catch(() => ({}));

      if (response.ok) {
        this.#showSuccess(form);
        return;
      }
      // A 400 or 429 carries a sentence meant for the customer; anything else
      // gets the block's own message.
      const message =
        (response.status === 400 || response.status === 429) && typeof answer.error === 'string'
          ? answer.error
          : this.dataset.error || '';
      this.#showError(form, message);
    } catch (error) {
      console.warn('[contact-form]', error);
      this.#showError(form, this.dataset.error || '');
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
    }
  };

  /**
   * Shows the confirmation in place of the fields, so the message cannot be
   * sent twice, and moves focus to it.
   * @param {HTMLFormElement} form
   */
  #showSuccess(form) {
    const success = form.querySelector('.contact-form__success');
    if (!(success instanceof HTMLElement)) return;
    for (const child of form.children) {
      if (child instanceof HTMLElement && child !== success) child.hidden = true;
    }
    success.hidden = false;
    success.focus();
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} text
   */
  #showError(form, text) {
    const error = form.querySelector('.contact-form__error[role="alert"]');
    const errorText = error?.querySelector('.contact-form__error-text');
    if (!(error instanceof HTMLElement) || !errorText) return;
    errorText.textContent = text;
    error.hidden = !text;
    if (text) error.focus();
  }
}

if (!customElements.get('contact-form-hub')) {
  customElements.define('contact-form-hub', ContactFormHub);
}
